import { Loader2, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import type {
  CriterionKey,
  DecisionPriorities,
  GermanLevel,
  Seniority,
  WorkMode,
} from "@/lib/types";
import { cn } from "@/lib/utils";

const CRITERIA: { key: CriterionKey; label: string; hint: string }[] = [
  {
    key: "fit",
    label: "CV fit",
    hint: "How much of the posting your CV shows",
  },
  {
    key: "language",
    label: "German",
    hint: "The German it asks for, against yours",
  },
  {
    key: "location",
    label: "Place and way of working",
    hint: "Your cities; remote, hybrid, on-site",
  },
  { key: "salary", label: "Salary", hint: "Only when the posting states one" },
  { key: "seniority", label: "Level", hint: "Junior, mid, senior or lead" },
];

const WEIGHTS = [
  { value: 0, label: "Ignore" },
  { value: 1, label: "Low" },
  { value: 2, label: "Normal" },
  { value: 3, label: "High" },
];

const GERMAN: { value: GermanLevel; label: string }[] = [
  { value: "none", label: "None" },
  { value: "basic", label: "Basic (A2–B1)" },
  { value: "good", label: "Good (B2)" },
  { value: "fluent", label: "Fluent (C1+)" },
];

const MODES: WorkMode[] = ["remote", "hybrid", "onsite"];
const LEVELS: Seniority[] = ["junior", "mid", "senior", "lead"];

function toggle<T>(list: T[], item: T): T[] {
  return list.includes(item)
    ? list.filter((entry) => entry !== item)
    : [...list, item];
}

/**
 * What matters to you when deciding whether to apply.
 *
 * A weight says how much a criterion counts; "must" makes it a deal-breaker –
 * failing it means Skip, whatever the rest says. Saving re-decides every
 * posting from what was already read, so it costs nothing.
 */
export function PrioritiesPanel({
  priorities,
  onSaved,
  onClose,
}: {
  priorities: DecisionPriorities;
  onSaved: (priorities: DecisionPriorities) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<DecisionPriorities>(priorities);
  const [cities, setCities] = useState(priorities.locations.join(", "));
  const [salary, setSalary] = useState(
    priorities.minSalaryEur === null ? "" : String(priorities.minSalaryEur),
  );
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const salaryNumber = salary.trim() === "" ? null : Number(salary);
  const salaryValid =
    salaryNumber === null ||
    (Number.isInteger(salaryNumber) &&
      salaryNumber >= 0 &&
      salaryNumber <= 1_000_000);

  async function save() {
    setBusy(true);
    setNote(null);
    try {
      const saved = await api.applications.setPriorities({
        ...draft,
        locations: cities
          .split(",")
          .map((city) => city.trim())
          .filter(Boolean),
        minSalaryEur: salaryNumber,
      });
      setDraft(saved);
      setCities(saved.locations.join(", "));
      onSaved(saved);
      setNote("Saved – every posting is re-decided.");
    } catch (reason) {
      setNote((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="space-y-4 rounded-2xl border bg-card/50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold">What matters to me</h2>
          <p className="text-xs text-muted-foreground">
            Weights decide how much each counts. A <em>must</em> is a
            deal-breaker. 70 and up is Apply, 45 and up Maybe.
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
          aria-label="Close priorities"
        >
          <X className="size-4" aria-hidden />
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="py-1 pr-3 font-medium">Criterion</th>
              <th className="py-1 pr-3 font-medium">Weight</th>
              <th className="py-1 font-medium">Must</th>
            </tr>
          </thead>
          <tbody>
            {CRITERIA.map((criterion) => (
              <tr key={criterion.key} className="border-t border-border/50">
                <td className="py-1.5 pr-3">
                  {criterion.label}
                  <span className="block text-xs text-muted-foreground">
                    {criterion.hint}
                  </span>
                </td>
                <td className="py-1.5 pr-3">
                  <div
                    className="flex gap-1"
                    role="radiogroup"
                    aria-label={`${criterion.label} weight`}
                  >
                    {WEIGHTS.map((weight) => (
                      <button
                        key={weight.value}
                        type="button"
                        role="radio"
                        aria-checked={
                          draft.weights[criterion.key] === weight.value
                        }
                        onClick={() =>
                          setDraft({
                            ...draft,
                            weights: {
                              ...draft.weights,
                              [criterion.key]: weight.value,
                            },
                          })
                        }
                        className={cn(
                          "rounded border px-2 py-0.5 text-xs transition-colors",
                          draft.weights[criterion.key] === weight.value
                            ? "border-primary text-primary"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {weight.label}
                      </button>
                    ))}
                  </div>
                </td>
                <td className="py-1.5">
                  <input
                    type="checkbox"
                    aria-label={`${criterion.label} is a must`}
                    checked={draft.musts.includes(criterion.key)}
                    onChange={() =>
                      setDraft({
                        ...draft,
                        musts: toggle(draft.musts, criterion.key),
                      })
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="block space-y-1 text-sm">
          <span className="text-muted-foreground">My German</span>
          <select
            className="h-9 w-full rounded-md border bg-transparent px-2 text-sm"
            value={draft.germanLevel}
            onChange={(event) =>
              setDraft({
                ...draft,
                germanLevel: event.target.value as GermanLevel,
              })
            }
          >
            {GERMAN.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-muted-foreground">
            Cities I would work in (empty = anywhere)
          </span>
          <Input
            value={cities}
            placeholder="Stuttgart, Karlsruhe"
            onChange={(event) => setCities(event.target.value)}
          />
        </label>
        <fieldset className="space-y-1 text-sm">
          <legend className="text-muted-foreground">
            Ways of working I accept
          </legend>
          <div className="flex flex-wrap gap-3">
            {MODES.map((mode) => (
              <label key={mode} className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={draft.workModes.includes(mode)}
                  onChange={() =>
                    setDraft({
                      ...draft,
                      workModes: toggle(draft.workModes, mode),
                    })
                  }
                />
                {mode}
              </label>
            ))}
          </div>
        </fieldset>
        <label className="block space-y-1 text-sm">
          <span className="text-muted-foreground">
            Minimum salary, EUR a year (empty = not a criterion)
          </span>
          <Input
            type="number"
            min={0}
            step={1000}
            value={salary}
            placeholder="e.g. 60000"
            onChange={(event) => setSalary(event.target.value)}
          />
        </label>
        <fieldset className="space-y-1 text-sm md:col-span-2">
          <legend className="text-muted-foreground">
            Levels I am after (none ticked = any)
          </legend>
          <div className="flex flex-wrap gap-3">
            {LEVELS.map((level) => (
              <label key={level} className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={draft.seniorities.includes(level)}
                  onChange={() =>
                    setDraft({
                      ...draft,
                      seniorities: toggle(draft.seniorities, level),
                    })
                  }
                />
                {level}
              </label>
            ))}
          </div>
        </fieldset>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          onClick={() => void save()}
          disabled={busy || !salaryValid}
        >
          {busy ? (
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
          ) : null}
          Save priorities
        </Button>
        {!salaryValid ? (
          <span className="text-xs text-destructive">
            Salary must be a whole number of euros.
          </span>
        ) : null}
        {note ? (
          <span className="text-xs text-muted-foreground">{note}</span>
        ) : null}
      </div>
    </section>
  );
}
